import {Chip} from '@dropbox/dig-components/chip';
import {Menu} from '@dropbox/dig-components/menu';
import {Truncate} from '@dropbox/dig-components/truncate';
import {atoms} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {WorkstreamsLine} from '@dropbox/dig-icons/assets';
import {useMemo} from 'react';
import {useWorkstreamAncestors} from 'views/workstreams/hooks';

/**
 * "N parents" chip for the properties-row sidebar layout: a dropdown
 * of every workstream this one rolls up into (its direct parent on each
 * breadcrumb path). Only rendered when there's more than one parent.
 */
export const SidebarPartOfMenu = ({workstreamId}: {workstreamId: number}) => {
  const {ancestorPaths} = useWorkstreamAncestors(workstreamId);
  const parents = useMemo(() => {
    const byId = new Map<number, {id: number; name: string}>();
    for (const path of ancestorPaths) {
      const parent = path[path.length - 1];
      if (parent && parent.id !== workstreamId) byId.set(parent.id, parent);
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [ancestorPaths, workstreamId]);

  if (parents.length < 2) return null;

  // "N parents", like the "N resources" chip beside it ("Part of N" read as
  // ambiguous).
  const label = `${parents.length} parents`;
  return (
    <Menu.Wrapper shouldPropagateClickOutsideMouseEvents>
      {({getContentProps, getTriggerProps}) => (
        <>
          <Chip {...getTriggerProps()} size="small" variant="standard" withDropdownIcon>
            <Chip.Content>{label}</Chip.Content>
          </Chip>
          <Menu.Content {...getContentProps()} placement="bottom-start" minWidth="240px">
            <Menu.Segment>
              {/* Links: hosts that intercept /workstreams/:id (the /workstreams
                  pane) open them in place; elsewhere they navigate. */}
              {parents.map((parent) => (
                <Menu.LinkItem
                  key={parent.id}
                  href={`/workstreams/${parent.id}`}
                  withLeftAccessory={
                    <UIIcon
                      src={WorkstreamsLine}
                      size="small"
                      className={atoms({color: 'Text Subtle'})}
                    />
                  }
                >
                  <Truncate maxWidth={280}>{parent.name}</Truncate>
                </Menu.LinkItem>
              ))}
            </Menu.Segment>
          </Menu.Content>
        </>
      )}
    </Menu.Wrapper>
  );
};
